import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { AppShell } from "../../components/AppShell";
import { EmptyState, LoadingCards, LoadingRows } from "../../components/states";
import { Button } from "../../components/ui/button";
import { signOutAndForgetDevice } from "../../features/auth/sign-out";
import { useAuthUser } from "../../features/auth/useAuthUser";
import { useSignOutToLanding } from "../../features/auth/use-sign-out";
import { EventExperiencePage } from "../../features/event-experience/EventExperiencePage";
import { useMembership } from "../../features/event-experience/use-membership";
import { ArchivedBanner } from "../../features/events/ArchivedBanner";
import { EventPreview } from "../../features/events/PublicEventPage";
import type { PublicEvent } from "../../features/events/schemas";
import { usePublicEvent } from "../../features/events/use-public-event";
import { RegisterControls } from "../../features/registration/RegisterControls";
import {
  registrationFailureMessage,
  registrationSuccessMessage,
} from "../../features/registration/command-feedback";
import { useRegistrationCommand } from "../../features/registration/use-registration-command";

export const Route = createFileRoute("/e/$slug")({
  component: RouteComponent,
});

function RouteComponent() {
  const { slug } = Route.useParams();
  const publicEvent = usePublicEvent(slug);
  const { user, loading: authLoading } = useAuthUser();
  const signOutToLanding = useSignOutToLanding();

  if (publicEvent.status === "loading" || authLoading) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page text-foreground">
        <div className="w-full max-w-2xl px-5">
          <LoadingRows rows={4} />
        </div>
      </main>
    );
  }

  if (publicEvent.status === "not-found") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page text-foreground p-6">
        <EmptyState
          icon={AlertCircle}
          title="Event not found"
          testId="event-not-found"
        >
          This event may have been unpublished or the link may be incorrect.
        </EmptyState>
      </main>
    );
  }

  if (publicEvent.status === "error") {
    return (
      <main className="flex min-h-screen items-center justify-center bg-gradient-page text-foreground p-6">
        <EmptyState
          icon={AlertCircle}
          title="Something went wrong"
          testId="event-error"
        >
          {publicEvent.error}
        </EmptyState>
      </main>
    );
  }

  if (!user) {
    // D21: the signed-out event page is no longer a login surface. One CTA,
    // wherever the event's gating state is — the sign-in route owns how the
    // person proves who they are, and its redirect brings them straight back.
    return (
      <EventPreview
        event={publicEvent.event}
        // A participant still signs in to read an archived event, so the card
        // stays; it just stops inviting people to join.
        action={
          <SignInCard
            slug={slug}
            archived={publicEvent.event.archived === true}
          />
        }
      />
    );
  }

  return (
    <MemberGate
      event={publicEvent.event}
      uid={user.uid}
      email={user.email}
      signOut={() => signOutToLanding(user.uid)}
      signOutHere={() => void signOutAndForgetDevice(user.uid)}
    />
  );
}

/**
 * The join block: a dark band in both themes. Dusk gives it the edge layer, so
 * the one action on the page stands out from the hero behind it; light keeps
 * a quiet 1px border and leaves the hard edge to the button, which is the
 * page's main call to action. The button's border width is written as a length
 * because tailwind-merge in `cn` reads `border-edge` as a colour and would drop
 * one of the two border classes.
 */
function SignInCard({ slug, archived }: { slug: string; archived: boolean }) {
  const navigate = useNavigate();

  return (
    <div
      className="w-full max-w-md rounded-lg border border-border bg-hb-dusk-deep p-5 text-white wrap-anywhere dark:border-edge dark:border-lit-edge dark:shadow-lit"
      data-testid="register-sign-in-band"
    >
      <h2 className="text-xl font-semibold text-white">
        {archived ? "Read this event" : "Join this event"}
      </h2>
      <p className="mb-5 mt-1 text-sm text-white/85">
        {archived
          ? "Sign in to read this event. Registration is closed."
          : "Sign in to register — with your work account, or a passwordless link if your organisation is not on Connect yet."}
      </p>
      <Button
        variant="cta"
        size="lg"
        // The ring is white on the dark join band, which the plain ring is not.
        className="w-full focus-visible:ring-white focus-visible:ring-offset-hb-dusk-deep"
        data-testid="register-sign-in-cta"
        onClick={() =>
          void navigate({
            to: "/sign-in",
            search: { redirect: `/e/${slug}` },
          })
        }
      >
        {archived ? "Sign in" : "Sign in to join"}
      </Button>
    </div>
  );
}

function MemberGate({
  event,
  uid,
  email,
  signOut,
  signOutHere,
}: {
  event: PublicEvent;
  uid: string;
  email: string | null;
  /** A member's sign-out: to "/", so the next sign-in starts fresh. */
  signOut: () => void;
  /**
   * The non-member preview's sign-out: stays on this page. Somebody who
   * opened a shared link signed in as the wrong account signs out here and
   * gets "Sign in to join", whose redirect brings them back. Not after a
   * withdrawal on this page, though: somebody who just left the event is
   * done with it, and their sign-out goes to "/" like a member's.
   */
  signOutHere: () => void;
}) {
  const [refetchKey, setRefetchKey] = useState(0);
  const membership = useMembership(event.eventId, uid, refetchKey);
  const command = useRegistrationCommand();
  const [withdrewHere, setWithdrewHere] = useState(false);

  useEffect(() => {
    if (command.state.status === "succeeded") {
      setWithdrewHere(command.state.type === "WITHDRAW_FROM_EVENT");
      const message = registrationSuccessMessage(command.state.type);
      setRefetchKey((key) => key + 1);
      command.reset();
      toast.success(message);
    } else if (command.state.status === "failed") {
      toast.error(registrationFailureMessage(command.state.error));
      command.reset();
    }
  }, [command.state.status, command]);

  const handleRegister = useCallback(
    (registrationSecret?: string) => {
      command.submit(
        "REGISTER_FOR_EVENT",
        event.eventId,
        uid,
        registrationSecret,
      );
    },
    [command, event.eventId, uid],
  );

  const handleWithdraw = useCallback(() => {
    command.submit("WITHDRAW_FROM_EVENT", event.eventId, uid);
  }, [command, event.eventId, uid]);

  const isCommandPending =
    command.state.status === "submitting" ||
    command.state.status === "watching";

  // A skeleton, not the preview. The preview is the page for somebody who is
  // NOT in this event, and rendering it while the membership read is still
  // outstanding showed a member a register button and non-member chrome for as
  // long as the round trip took.
  if (membership.status === "loading") {
    return (
      <AppShell identity={{ email }} onSignOut={signOut} title={event.title}>
        <div data-testid="membership-loading">
          <LoadingCards cards={2} />
        </div>
      </AppShell>
    );
  }

  if (membership.status === "success") {
    return (
      <>
        {event.archived === true && <ArchivedBanner variant="strip" />}
        <EventExperiencePage
          event={event}
          membership={membership.data}
          email={email}
          signOut={signOut}
          onWithdraw={handleWithdraw}
          withdrawState={command.state}
          onMembershipChanged={() => setRefetchKey((key) => key + 1)}
        />
      </>
    );
  }

  return (
    <EventPreview
      event={event}
      session={{ email, onSignOut: withdrewHere ? signOut : signOutHere }}
      action={
        // Registration is closed for an archived event (the banner says so).
        event.archived === true ? undefined : (
          <RegisterControls
            secretRequired={event.registrationSecretEnabled === true}
            pending={isCommandPending}
            onRegister={handleRegister}
          />
        )
      }
    />
  );
}
